/* Карточка «Ещё настройки» экрана движений (§20.1 п. 6, общая StudioSettings §1.5): слоты «Голос» и «Фон».
   «Голос» — «Как в видео · Без изменений»; «Голос героя» (у персонажа — его голос, у варианта «Вы» — его; у «Вы», близкого и фото — неактивна
   с причиной), выбранный голос каталога строкой и «Другой голос ›» (только голоса с переозвучкой) — строки стоят, только если форма публикует
   значения hero / voice (+ поле voice_id) и policy.voice_change_available; надбавка «+N C в минуту» — только из политики. Ролик без звука —
   слот закреплён «Без звука». «Фон» — значения поля bg (и policy.backgrounds) строками с пояснением и пределом фрагмента. */
import { useState } from 'react';
import type { Voice } from '../../api/types';
import { uiFormat, uiText } from '../../i18n';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { ToolIcon } from '../studio/circle/ToolIcon';
import { StudioSettings, StudioSettingsSection, type StudioSettingSlot } from '../studio/StudioFormParts';
import { toolField } from '../studio/circle/tool-form';
import { VoicePick } from '../tts/VoicePick';
import { motionLimits, motionPolicy, motionVoiceKey, motionVoiceModes, motionVoiceUnit, type MotionHeroKind } from './motion-model';

function Row({
  on,
  title,
  sub,
  disabled,
  why,
  onPick,
}: {
  on: boolean;
  title: string;
  sub: string;
  disabled?: boolean;
  why?: string;
  onPick(): void;
}) {
  const [shown, setShown] = useState(false);
  return (
    <>
      <button
        type="button"
        className={`sc-r${on ? ' on' : ''}${disabled ? ' off' : ''}`}
        aria-pressed={on}
        aria-disabled={disabled ? 'true' : undefined}
        onClick={() => {
          if (disabled) setShown(v => !v);
          else onPick();
        }}
      >
        <span className="m">
          <b>{uiText(title)}</b>
          <small>{uiText(sub)}</small>
        </span>
        <span className="rd" aria-hidden="true">
          <ToolIcon kind="check" />
        </span>
      </button>
      {disabled && shown && why && (
        <p className="studio-settings-tip" role="status">
          {uiText(why)}
        </p>
      )}
    </>
  );
}
const heroWhy = (kind: MotionHeroKind | null) =>
  kind === 'me'
    ? 'У «Вы» нет своего голоса: клонируйте его в разделе «Клон» или выберите другой'
    : kind === 'person' || kind === 'ph'
      ? 'У человека с фото нет своего голоса: выберите другой голос'
      : kind
        ? 'У героя нет своего голоса: выберите другой'
        : 'Сначала выберите, кто повторит';
const bgHero = (kind: MotionHeroKind | null) =>
  kind === 'char'
    ? 'Фон с картинки персонажа'
    : kind === 'person'
      ? 'Фон с фото близкого'
      : kind === 'ph' || kind === 'me' || kind === 'avatar'
        ? 'Фон с вашего фото'
        : 'Фон со снимка героя';

export function MotionSettings({
  state,
  hero,
  heroVoice,
  mute,
  voice,
  onVoice,
}: {
  state: CircleFormState;
  hero: MotionHeroKind | null;
  /** Голос героя (id), если он у героя есть */
  heroVoice: number | null;
  /** В ролике нет звука (известно точно) */
  mute: boolean;
  voice: Voice | null;
  onVoice(voice: Voice | null): void;
}) {
  const { form, inputs, locked } = state;
  if (!form) return null;
  const policy = motionPolicy(form);
  const voiceField = toolField(form, 'voice', 'voice_mode');
  const voiceKey = motionVoiceKey(form);
  const modes = motionVoiceModes(form);
  const unit = motionVoiceUnit(form);
  const mode = inputs[voiceKey] ?? 'src';
  const bgField = toolField(form, 'bg');
  const allowedBg = Array.isArray(policy?.backgrounds) ? (policy!.backgrounds as unknown[]).map(String) : ['video', 'hero'];
  const bgs = (bgField?.values ?? []).filter(v => ['video', 'hero'].includes(v) && allowedBg.includes(v));
  const bg = inputs.bg === 'hero' ? 'hero' : 'video';
  const audioKept = policy?.original_audio_preserved === true;
  const set = (key: string, value: string) => state.setInputs(v => ({ ...v, [key]: value }));
  const slots: StudioSettingSlot[] = [];
  if (voiceField) {
    if (mute) slots.push({ title: 'Голос', value: 'Без звука', kind: 'info', lock: true, tip: 'В видео нет звука: менять голос нечего' });
    else if (!audioKept && modes.length < 2)
      slots.push({ title: 'Голос', value: 'Без звука', kind: 'info', lock: true, tip: 'Звук видео в результат пока не переносится' });
    else
      slots.push({
        title: 'Голос',
        value: mode === 'hero' ? 'Голос героя' : mode === 'voice' && voice ? voice.name : 'Как в видео',
        section: 'voice',
      });
  }
  if (bgs.length) slots.push({ title: 'Фон', value: bg === 'hero' ? 'С героя' : 'Из видео', section: 'bg', disabled: bgs.length < 2 });
  if (!slots.length) return null;
  const pickMode = (value: string) => {
    state.setInputs(v => {
      const next = { ...v, [voiceKey]: value };
      if (value !== 'voice') delete next.voice_id;
      return next;
    });
    if (value !== 'voice') onVoice(null);
  };
  return (
    <StudioSettings disabled={locked} slots={slots}>
      {voiceField && !mute && (audioKept || modes.length > 1) && (
        <StudioSettingsSection id="voice" title="Голос" hint={unit !== null && modes.length > 1 ? uiFormat('+{0} C в минуту', unit) : undefined}>
          <div className="sc-list">
            <Row on={mode === 'src'} title="Как в видео" sub="Без изменений" disabled={locked} onPick={() => pickMode('src')} />
            {modes.includes('hero') && (
              <Row
                on={mode === 'hero'}
                title="Голос героя"
                sub={heroVoice !== null ? 'Свой голос героя' : 'У героя нет своего голоса'}
                disabled={locked || heroVoice === null}
                why={heroVoice === null ? heroWhy(hero) : undefined}
                onPick={() => pickMode('hero')}
              />
            )}
            {modes.includes('voice') && voice && (
              <Row on={mode === 'voice'} title={voice.name} sub="из каталога · сменить" disabled={locked} onPick={() => pickMode('voice')} />
            )}
          </div>
          {modes.includes('voice') && (
            <VoicePick
              value={voice}
              s2s
              disabled={locked}
              sub="Другой голос ›"
              onPick={picked => {
                onVoice(picked);
                state.setInputs(v => ({ ...v, [voiceKey]: 'voice', voice_id: String(picked.id) }));
              }}
            />
          )}
        </StudioSettingsSection>
      )}
      {bgs.length > 0 && (
        <StudioSettingsSection id="bg" title="Фон">
          <div className="sc-list">
            {bgs.map(value => {
              const limit = motionLimits(form, { ...inputs, bg: value });
              return (
                <Row
                  key={value}
                  on={bg === value}
                  title={value === 'video' ? 'Из видео' : 'С героя'}
                  sub={`${uiText(value === 'video' ? 'Сцена вашего видео, герой встанет на место человека' : bgHero(hero))} · ${uiFormat('фрагмент до {0} секунд', limit.maxMs / 1000)}`}
                  disabled={locked}
                  onPick={() => set('bg', value)}
                />
              );
            })}
          </div>
        </StudioSettingsSection>
      )}
    </StudioSettings>
  );
}
